import { createBrowserRouter, Navigate } from 'react-router-dom'

import { ComingSoonPage } from '@/components/feedback/ComingSoonPage'
import { RouteError } from '@/components/feedback/RouteError'
import { AppShell } from '@/components/layout/AppShell'
import { PlatformShell } from '@/components/layout/PlatformShell'
import { AdminRoute } from '@/app/router/AdminRoute'
import { AreaRoute } from '@/app/router/AreaRoute'
import { AnalyticsRoute } from '@/app/router/AnalyticsRoute'
import { WebsiteFormRoute } from '@/app/router/WebsiteFormRoute'
import { CatalogueRoute } from '@/app/router/CatalogueRoute'
import { ProtectedRoute } from '@/app/router/ProtectedRoute'
import { paths } from '@/app/router/paths'
import { handles } from '@/app/router/routeHandles'

/** Every page is its own chunk — the sign-in screen carries nothing else. */
export const router = createBrowserRouter([
  {
    path: paths.login,
    errorElement: <RouteError />,
    lazy: async () => ({ Component: (await import('@/features/auth/pages/LoginPage')).LoginPage }),
  },
  {
    path: `${paths.invitations}/:token`,
    errorElement: <RouteError />,
    lazy: async () => ({
      Component: (await import('@/features/auth/pages/InvitationPage')).InvitationPage,
    }),
  },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AreaRoute area="platform" />,
        children: [
          {
            path: paths.admin,
            element: <PlatformShell />,
            children: [
              {
                index: true,
                element: <Navigate to={paths.adminProducts} replace />,
              },
              {
                path: paths.adminProducts,
                handle: handles.adminProducts,
                lazy: async () => ({
                  Component: (await import('@/features/platform/pages/AblefyProductsPage'))
                    .AblefyProductsPage,
                }),
              },
              { path: '*', element: <RouteError /> },
            ],
          },
        ],
      },
      {
        element: <AreaRoute area="tenant" />,
        children: [
          {
            element: <AppShell />,
            children: [
              {
                index: true,
                element: <Navigate to={paths.pipeline} replace />,
              },
              {
                path: paths.pipeline,
                handle: handles.pipeline,
                lazy: async () => ({
                  Component: (await import('@/features/pipeline/pages/PipelinePage')).PipelinePage,
                }),
              },
              {
                path: paths.calls,
                handle: handles.calls,
                lazy: async () => ({
                  Component: (await import('@/features/calls/pages/CallsPage')).CallsPage,
                }),
              },
              {
                path: paths.customers,
                handle: handles.customers,
                lazy: async () => ({
                  Component: (await import('@/features/customers/pages/CustomersPage'))
                    .CustomersPage,
                }),
              },
              {
                path: `${paths.customers}/:customerId`,
                handle: handles.customerDetail,
                lazy: async () => ({
                  Component: (await import('@/features/customers/pages/CustomerProfilePage'))
                    .CustomerProfilePage,
                }),
              },
              {
                path: paths.scheduler,
                handle: handles.scheduler,
                lazy: async () => ({
                  Component: (await import('@/features/scheduler/pages/SchedulerPage'))
                    .SchedulerPage,
                }),
              },
              { path: paths.contracts, handle: handles.contracts, element: <ComingSoonPage /> },
              {
                path: paths.compliance,
                handle: handles.compliance,
                lazy: async () => ({
                  Component: (await import('@/features/compliance/pages/CompliancePage'))
                    .CompliancePage,
                }),
              },
              {
                element: <AnalyticsRoute />,
                children: [
                  {
                    path: paths.analytics,
                    handle: handles.analytics,
                    lazy: async () => ({
                      Component: (await import('@/features/analytics/pages/AnalyticsPage'))
                        .AnalyticsPage,
                    }),
                  },
                ],
              },
              {
                element: <CatalogueRoute />,
                children: [
                  {
                    path: paths.ablefyProducts,
                    handle: handles.ablefyProducts,
                    lazy: async () => ({
                      Component: (await import('@/features/platform/pages/AblefyProductsPage'))
                        .AblefyProductsPage,
                    }),
                  },
                ],
              },
              {
                element: <AdminRoute area="company" />,
                children: [
                  {
                    path: paths.companyAdmin,
                    handle: handles.companyAdmin,
                    lazy: async () => ({
                      Component: (await import('@/features/admin/pages/CompanyAdminPage'))
                        .CompanyAdminPage,
                    }),
                  },
                ],
              },
              {
                element: <AdminRoute area="franchise" />,
                children: [
                  {
                    path: paths.franchiseAdmin,
                    handle: handles.franchiseAdmin,
                    lazy: async () => ({
                      Component: (await import('@/features/admin/pages/FranchiseAdminPage'))
                        .FranchiseAdminPage,
                    }),
                  },
                ],
              },
              {
                path: paths.settings,
                handle: handles.settings,
                lazy: async () => ({
                  Component: (await import('@/features/settings/pages/SettingsPage')).SettingsPage,
                }),
              },
              {
                element: <WebsiteFormRoute />,
                children: [
                  {
                    path: paths.websiteForm,
                    handle: handles.websiteForm,
                    lazy: async () => ({
                      Component: (await import('@/features/settings/pages/WebsiteFormPage'))
                        .WebsiteFormPage,
                    }),
                  },
                ],
              },
              { path: '*', element: <RouteError /> },
            ],
          },
        ],
      },
    ],
  },
])
